// src/pages/PropertyPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { updatePageSeo } from '../components/SeoManager';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// Fallback image (inline SVG)
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1400" height="900" viewBox="0 0 1400 900">
    <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#cbd5e1"/></linearGradient></defs>
    <rect width="1400" height="900" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="700" y="430" font-size="32" font-weight="700">Property</text>
      <text x="700" y="470" font-size="16" opacity="0.85">Image not available</text>
    </g>
  </svg>`);

// helpers
function currencyGBP(value, text) {
  if (text) return text;
  if (value == null) return 'POA';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  } catch {
    return `£${Number(value).toLocaleString('en-GB')}`;
  }
}
function fmtAuction(d) {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}
function sanitizeHtml(html = '') {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}
function toMapHref(addr, lat, lng) {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lat + ',' + lng)}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || '')}`;
}

export default function PropertyPage() {
  const { slug } = useParams();

  const [prop, setProp] = useState(null);
  const [gallery, setGallery] = useState([]); // array of URLs
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // enquiry state
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submit, setSubmit] = useState({ state: 'idle', msg: '' });

  // main fetch
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        // Get the property
        const res = await fetch(`${API_BASE}/properties.php?slug=${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || 'Property not found');
        if (cancel) return;
        const p = json.data;

        // Build gallery: try dedicated media endpoint; fall back to primary image
        let images = [];
        try {
          const mres = await fetch(`${API_BASE}/property_media.php?property_id=${encodeURIComponent(p.id)}`);
          if (mres.ok) {
            const mjson = await mres.json();
            if (mjson.ok && Array.isArray(mjson.data) && mjson.data.length) {
              images = mjson.data.map(x => x.full_image_url || x.image_url).filter(Boolean);
            }
          }
        } catch {}
        if (!images.length) {
          images = [p.full_image_url || p.image_url || PLACEHOLDER];
        }

        setProp(p);
        setGallery(images);

        // Similar props (same listing_type, exclude current id)
        try {
          const sres = await fetch(`${API_BASE}/properties.php?status=active&listing_type=${encodeURIComponent(p.listing_type)}&limit=6`);
          if (sres.ok) {
            const sjson = await sres.json();
            if (sjson.ok) {
              const list = (sjson.data || []).filter(x => x.id !== p.id).slice(0, 3);
              setSimilar(list);
            }
          }
        } catch {}
      } catch (e) {
        if (!cancel) {
          setErr(e.message || 'Failed to load');
          setProp(null);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  // gallery state
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [slug]);
  const next = () => setIdx((i) => (i + 1) % Math.max(1, gallery.length));
  const prev = () => setIdx((i) => (i - 1 + Math.max(1, gallery.length)) % Math.max(1, gallery.length));

  // derived
  const cover = gallery[idx] || PLACEHOLDER;
  const bodyHtml = useMemo(() => sanitizeHtml(prop?.description_html || prop?.description || ''), [prop]);
  const priceLabel = prop?.listing_type === 'rent' ? 'Rent' : (prop?.listing_type === 'auction' ? 'Guide Price' : 'Price');
  const priceDisplay = currencyGBP(prop?.price, prop?.price_text);
  const isAuction = (prop?.listing_type || '').toLowerCase() === 'auction' || !!prop?.auction_date;
  const addressLine = [prop?.titleAddress, prop?.town, prop?.postcode].filter(Boolean).join(', '); // titleAddress optional

  useEffect(() => {
    if (!prop?.title) return;
    const details = [prop.beds ? `${prop.beds} bedroom` : '', prop.listing_type === 'rent' ? 'property to rent' : 'property for sale', prop.town, prop.postcode].filter(Boolean).join(' in ');
    updatePageSeo({
      title: `${prop.title} | MRM Lettings`,
      description: `View this ${details}. See photographs, property details and contact MRM Lettings to enquire.`.slice(0, 160),
      path: `/property/${prop.slug || slug}`,
      image: prop.full_image_url || prop.image_url || '/media/properties/property-1.jpg',
    });
  }, [prop, slug]);

  // form handlers
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setSubmit({ state: 'error', msg: 'Please fill name, email and message.' });
      return;
    }
    try {
      setSubmit({ state: 'loading', msg: 'Sending…' });
      const res = await fetch(`${API_BASE}/property_enquiry.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: prop.id, slug: prop.slug, ...form }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.ok === false) throw new Error(json.error || `HTTP ${res.status}`);
      setSubmit({ state: 'success', msg: 'Thank you — we’ll be in touch shortly.' });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (er) {
      setSubmit({ state: 'error', msg: er.message || 'Something went wrong. Please try again.' });
    }
  };

  if (!loading && err) {
    return (
      <div className="mrm-prop" style={{ padding: '4rem 1rem', fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ margin: 0 }}>Property not found</h1>
          <p style={{ color: '#475569' }}>{err}</p>
          <a href="/properties" style={{ color: '#1e3a8a', fontWeight: 800, textDecoration: 'none' }}>Back to listings</a>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ===== Property Page (namespaced) ===== */
        .mrm-prop { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:#0f172a; }
        .mrm-prop__hero {
          position:relative; background:#0b1220; color:#fff; overflow:hidden;
        }
        .mrm-prop__hero-media { position:relative; min-height: 54vh; }
        .mrm-prop__hero-media img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
        .mrm-prop__hero-gradient { content:""; position:absolute; inset:0; background: linear-gradient(180deg, rgba(0,0,0,.1) 0%, rgba(0,0,0,.6) 100%); }
        .mrm-prop__hero-inner { position:relative; z-index:1; padding: 1rem; max-width: 1100px; margin: 0 auto; display:grid; gap:.6rem; }
        .mrm-prop__badge-row { display:flex; gap:.5rem; flex-wrap:wrap; margin-top:.75rem; }
        .mrm-prop__badge { display:inline-flex; align-items:center; gap:.5rem; background: rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.2); color:#fff; padding:.35rem .6rem; border-radius:999px; font-weight:800; font-size:.82rem; }
        .mrm-prop__title { margin: .2rem 0 0; font-weight:900; font-size: clamp(1.4rem, 3.4vw, 2.2rem); line-height:1.1; text-shadow:0 2px 14px rgba(0,0,0,.25); }
        .mrm-prop__price { font-weight:900; color:#fff; font-size: clamp(1.2rem, 3vw, 1.8rem); }
        .mrm-prop__meta { display:flex; gap:1rem; flex-wrap:wrap; color:#e2e8f0; }
        .mrm-prop__meta-item { display:inline-flex; align-items:center; gap:.5rem; }
        .mrm-prop__controls { position:absolute; inset:auto 0 12px 0; display:flex; justify-content:space-between; pointer-events:none; padding: 0 1rem; max-width:1100px; margin: 0 auto; }
        .mrm-prop__btn {
          pointer-events:auto; border:none; cursor:pointer; width:40px; height:40px; border-radius:999px;
          background: rgba(255,255,255,.85); display:grid; place-items:center; font-weight:800;
          transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
        }
        .mrm-prop__btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(2,6,23,.25); background:#fff; }
        .mrm-prop__thumbs { display:flex; gap:.5rem; overflow:auto; padding: .6rem 1rem 1rem; scroll-snap-type: x mandatory; }
        .mrm-prop__thumb { width:90px; height:70px; border-radius:8px; overflow:hidden; flex:0 0 auto; scroll-snap-align: start; border:2px solid transparent; cursor:pointer; }
        .mrm-prop__thumb img { width:100%; height:100%; object-fit:cover; display:block; }
        .mrm-prop__thumb--active { border-color:#1e3a8a; }

        .mrm-prop__wrap { max-width: 1100px; margin: 0 auto; padding: 1.2rem; display:grid; gap: 1.2rem; }
        .mrm-prop__grid { display:grid; grid-template-columns: 1fr; gap: 1.2rem; }
        @media (min-width: 1024px){ .mrm-prop__grid { grid-template-columns: 2fr 1fr; } }

        .mrm-prop__card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 10px 24px rgba(2,6,23,.06); overflow:hidden; }
        .mrm-prop__card-body { padding: 1rem 1.2rem; }
        .mrm-prop__section-title { margin:0 0 .6rem; font-weight:900; font-size:1.12rem; color:#0f172a; }

        .mrm-prop__desc { color:#0f172a; line-height:1.75; font-size:1.02rem; }
        .mrm-prop__desc h2 { font-size:1.25rem; margin:1.4rem 0 .6rem; }
        .mrm-prop__desc h3 { font-size:1.1rem; margin:1rem 0 .45rem; }
        .mrm-prop__desc p { margin: .7rem 0; }
        .mrm-prop__desc ul, .mrm-prop__desc ol { padding-left: 1.25rem; margin: .5rem 0 .8rem; }

        .mrm-prop__facts { display:grid; grid-template-columns: 1fr 1fr; gap:.6rem; }
        @media (min-width:640px){ .mrm-prop__facts { grid-template-columns: 1fr 1fr 1fr; } }
        .mrm-prop__fact { background:#f8fafc; border:1px solid #e5e7eb; border-radius:10px; padding:.6rem .7rem; }
        .mrm-prop__fact b { display:block; font-size:.78rem; color:#475569; }
        .mrm-prop__fact span { font-weight:800; color:#0f172a; }

        .mrm-prop__map { display:block; border-radius:12px; overflow:hidden; border:1px solid #e5e7eb; }
        .mrm-prop__map img { width:100%; height: 240px; object-fit:cover; display:block; }

        .mrm-prop__enq label { display:block; font-size:.9rem; color:#334155; font-weight:700; margin:.35rem 0 .25rem; }
        .mrm-prop__input, .mrm-prop__textarea {
          width:100%; border:1px solid #cbd5e1; border-radius:10px; padding:.7rem .85rem; font-size:.95rem; outline:none;
          transition: border-color .2s, box-shadow .2s;
        }
        .mrm-prop__input:focus, .mrm-prop__textarea:focus { border-color:#1e3a8a; box-shadow:0 0 0 3px rgba(30,58,138,.15); }
        .mrm-prop__textarea { min-height: 120px; resize: vertical; }
        .mrm-prop__btn-submit {
          margin-top:.6rem; border:none; cursor:pointer; background:#1e3a8a; color:#fff; font-weight:900; border-radius:999px;
          padding:.8rem 1.1rem; font-size:.95rem; transition: transform .15s, box-shadow .2s, background .2s;
        }
        .mrm-prop__btn-submit:hover { transform: translateY(-1px); box-shadow:0 10px 22px rgba(30,58,138,.25); background:#173074; }
        .mrm-prop__status-ok { color:#166534; margin-top:.4rem; font-weight:700; }
        .mrm-prop__status-err { color:#991b1b; margin-top:.4rem; font-weight:700; }

        .mrm-prop__more { background:#f8fafc; padding: 1.2rem; border-radius:14px; border:1px solid #e5e7eb; }
        .mrm-prop__more-grid { display:grid; grid-template-columns:1fr; gap:.9rem; }
        @media (min-width:768px){ .mrm-prop__more-grid { grid-template-columns:1fr 1fr 1fr; } }
        .mrm-prop__card-mini { background:#fff; border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; }
        .mrm-prop__thumb-mini { height:130px; background:#e5e7eb; overflow:hidden; }
        .mrm-prop__thumb-mini img { width:100%; height:100%; object-fit:cover; display:block; }
        .mrm-prop__mini-body { padding:.7rem .8rem; }
        .mrm-prop__mini-title { margin:0; font-weight:800; font-size:.98rem; color:#0f172a; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height: 2.6em; }
        .mrm-prop__mini-meta { color:#64748b; font-size:.85rem; margin-top:.2rem; }
      `}</style>

      <div className="mrm-prop">
        {/* HERO + GALLERY */}
        <header className="mrm-prop__hero" aria-label="Property gallery and headline">
          <div className="mrm-prop__hero-media">
            <img
              src={cover}
              alt={prop?.title || 'Property image'}
              onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
              loading="eager"
            />
            <div className="mrm-prop__hero-gradient" />
            <div className="mrm-prop__hero-inner">
              <div className="mrm-prop__badge-row">
                {prop?.listing_type && <span className="mrm-prop__badge">{String(prop.listing_type).toUpperCase()}</span>}
                {prop?.town && <span className="mrm-prop__badge">{prop.town}{prop.postcode ? ` · ${prop.postcode}` : ''}</span>}
                {isAuction && prop?.auction_date && <span className="mrm-prop__badge">Auction · {fmtAuction(prop.auction_date)}</span>}
              </div>
              <h1 className="mrm-prop__title">{prop?.title || 'Loading…'}</h1>
              <div className="mrm-prop__price">
                <span style={{ fontWeight: 700, opacity: 0.9, marginRight: 8 }}>{priceLabel}:</span> {priceDisplay}
              </div>
              <div className="mrm-prop__meta">
                <div className="mrm-prop__meta-item">🛏 {prop?.beds ?? 0} bed</div>
                <div className="mrm-prop__meta-item">🛁 {prop?.baths ?? 0} bath</div>
                {prop?.tenure && <div className="mrm-prop__meta-item">📄 {prop.tenure}</div>}
              </div>
            </div>

            {/* gallery controls */}
            <div className="mrm-prop__controls">
              <button className="mrm-prop__btn" type="button" onClick={prev} aria-label="Previous image">‹</button>
              <button className="mrm-prop__btn" type="button" onClick={next} aria-label="Next image">›</button>
            </div>
          </div>

          {/* thumbnails */}
          <div className="mrm-prop__thumbs" role="tablist" aria-label="Gallery thumbnails">
            {gallery.map((src, i) => (
              <button
                key={src + i}
                className={`mrm-prop__thumb ${i === idx ? 'mrm-prop__thumb--active' : ''}`}
                onClick={() => setIdx(i)}
                aria-label={`View image ${i + 1}`}
              >
                <img
                  src={src}
                  alt={`Thumbnail ${i + 1}`}
                  onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </header>

        {/* BODY GRID */}
        <div className="mrm-prop__wrap">
          <div className="mrm-prop__grid">
            {/* LEFT: details */}
            <div className="mrm-prop__stack">
              {/* Facts */}
              <div className="mrm-prop__card">
                <div className="mrm-prop__card-body">
                  <h2 className="mrm-prop__section-title">Property details</h2>
                  <div className="mrm-prop__facts">
                    <div className="mrm-prop__fact"><b>Bedrooms</b><span>{prop?.beds ?? '-'}</span></div>
                    <div className="mrm-prop__fact"><b>Bathrooms</b><span>{prop?.baths ?? '-'}</span></div>
                    <div className="mrm-prop__fact"><b>Type</b><span>{prop?.listing_type ? prop.listing_type.toUpperCase() : '-'}</span></div>
                    <div className="mrm-prop__fact"><b>Town</b><span>{prop?.town || '-'}</span></div>
                    <div className="mrm-prop__fact"><b>Postcode</b><span>{prop?.postcode || '-'}</span></div>
                    {isAuction && <div className="mrm-prop__fact"><b>Auction</b><span>{fmtAuction(prop?.auction_date)}</span></div>}
                  </div>
                </div>
              </div>

              {/* Description */}
              {(prop?.description || prop?.description_html) && (
                <div className="mrm-prop__card">
                  <div className="mrm-prop__card-body">
                    <h2 className="mrm-prop__section-title">Description</h2>
                    <article
                      className="mrm-prop__desc"
                      dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                  </div>
                </div>
              )}

              {/* Map */}
              {(prop?.lat != null || prop?.address || addressLine) && (
                <a
                  className="mrm-prop__map mrm-prop__card"
                  href={toMapHref(prop?.address || addressLine, prop?.lat, prop?.lng)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open location in Google Maps"
                >
                  <img
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
                      prop?.lat != null && prop?.lng != null ? `${prop.lat},${prop.lng}` : (prop?.address || addressLine || '')
                    )}&zoom=15&size=1200x300&maptype=roadmap&markers=color:red%7C${encodeURIComponent(
                      prop?.lat != null && prop?.lng != null ? `${prop.lat},${prop.lng}` : (prop?.address || addressLine || '')
                    )}&key=AIzaSyB2-q_0T2LB2sA1X_syazc4Y-yZ-IdmSvo`}
                    alt="Map preview"
                    onError={(e) => { e.currentTarget.style.opacity = '0.6'; }}
                    loading="lazy"
                  />
                </a>
              )}
            </div>

            {/* RIGHT: enquiry */}
            <aside>
              <div className="mrm-prop__card">
                <div className="mrm-prop__card-body">
                  <h2 className="mrm-prop__section-title">Enquire about this property</h2>
                  <form className="mrm-prop__enq" onSubmit={onSubmit} noValidate>
                    <label htmlFor="name">Full name *</label>
                    <input id="name" name="name" className="mrm-prop__input" value={form.name} onChange={onChange} required />

                    <label htmlFor="email">Email *</label>
                    <input id="email" name="email" type="email" className="mrm-prop__input" value={form.email} onChange={onChange} required />

                    <label htmlFor="phone">Phone</label>
                    <input id="phone" name="phone" className="mrm-prop__input" value={form.phone} onChange={onChange} />

                    <label htmlFor="message">Message *</label>
                    <textarea id="message" name="message" className="mrm-prop__textarea" value={form.message} onChange={onChange} required />

                    <button type="submit" className="mrm-prop__btn-submit" disabled={submit.state === 'loading'}>
                      {submit.state === 'loading' ? 'Sending…' : 'Send enquiry'}
                    </button>

                    {submit.state === 'success' && <div className="mrm-prop__status-ok">✅ {submit.msg}</div>}
                    {submit.state === 'error' && <div className="mrm-prop__status-err">⚠️ {submit.msg}</div>}
                  </form>
                </div>
              </div>
            </aside>
          </div>

          {/* Similar */}
          {similar.length > 0 && (
            <section className="mrm-prop__more" aria-label="Similar properties">
              <h3 className="mrm-prop__section-title" style={{ marginBottom: '.6rem' }}>Similar properties</h3>
              <div className="mrm-prop__more-grid">
                {similar.map((s) => {
                  const sImg = s.full_image_url || s.image_url || PLACEHOLDER;
                  const sHref = s.href || (s.slug ? `/property/${s.slug}` : '#');
                  return (
                    <a key={s.id} className="mrm-prop__card-mini" href={sHref}>
                      <div className="mrm-prop__thumb-mini">
                        <img
                          src={sImg}
                          alt={s.title}
                          onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                          loading="lazy"
                        />
                      </div>
                      <div className="mrm-prop__mini-body">
                        <h4 className="mrm-prop__mini-title">{s.title}</h4>
                        <div className="mrm-prop__mini-meta">
                          {s.town ? `${s.town}${s.postcode ? ` · ${s.postcode}` : ''}` : (s.postcode || '')}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* JSON-LD schema */}
      {!loading && prop?.title && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'RealEstateListing',
              url: typeof window !== 'undefined' ? window.location.href : undefined,
              name: prop.title,
              description: prop.excerpt || undefined,
              address: prop.address || [prop.town, prop.postcode].filter(Boolean).join(', '),
              image: gallery,
              offers: {
                '@type': 'Offer',
                price: prop.price || undefined,
                priceCurrency: 'GBP',
                availability: 'https://schema.org/InStock',
              },
              numberOfRoomsTotal: prop.beds || undefined
            })
          }}
        />
      )}
    </>
  );
}
